import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const containerStyle = {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Poppins', sans-serif",
    color: '#2d3436',
    backgroundColor: '#ffffff',
    minHeight: '100vh'
  };

  const headerStyle = {
    textAlign: 'center',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
    borderRadius: '12px',
    marginBottom: '30px',
    color: 'white',
    boxShadow: '0 10px 20px rgba(0, 184, 148, 0.3)'
  };

  const titleStyle = {
    fontSize: 'clamp(2rem, 5vw, 2.8rem)',
    fontWeight: '700',
    marginBottom: '10px',
    letterSpacing: '0.5px'
  };

  const subtitleStyle = {
    fontSize: 'clamp(0.9rem, 2.5vw, 1.2rem)',
    fontWeight: '300',
    opacity: '0.9',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.5'
  };

  const tabContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '30px',
    justifyContent: 'center'
  };

  const tabStyle = {
    padding: '12px 24px',
    borderRadius: '30px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    backgroundColor: '#dfe6e9',
    color: '#636e72',
    fontSize: '0.95rem'
  };

  const activeTabStyle = {
    ...tabStyle,
    backgroundColor: '#00b894',
    color: 'white',
    boxShadow: '0 4px 8px rgba(0, 184, 148, 0.4)'
  };

  const contentStyle = {
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
    marginBottom: '40px'
  };

  const sectionTitleStyle = {
    fontSize: 'clamp(1.4rem, 4vw, 1.8rem)',
    fontWeight: '600',
    color: '#2d3436',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #00b894'
  };

  const textStyle = {
    fontSize: '1rem',
    lineHeight: '1.7',
    marginBottom: '20px',
    color: '#2d3436'
  };

  const listStyle = {
    paddingLeft: '20px',
    marginBottom: '20px'
  };

  const listItemStyle = {
    marginBottom: '12px',
    position: 'relative',
    paddingLeft: '25px',
    lineHeight: '1.6'
  };

  const listIconStyle = {
    position: 'absolute',
    left: '0',
    color: '#00b894',
    fontWeight: 'bold'
  };

  const highlightBoxStyle = {
    backgroundColor: '#d4f5e9',
    borderLeft: '4px solid #00b894',
    borderRadius: '8px',
    padding: '20px',
    margin: '25px 0',
    fontSize: '0.95rem'
  };

  const warningBoxStyle = {
    backgroundColor: '#ffebee',
    borderLeft: '4px solid #e17055',
    borderRadius: '8px',
    padding: '20px',
    margin: '25px 0',
    fontSize: '0.95rem'
  };

  const contactBoxStyle = {
    padding: '25px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
  };

  const linkStyle = {
    display: 'inline-block',
    padding: '12px 25px',
    backgroundColor: '#00b894',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    marginTop: '15px',
    ':hover': {
      backgroundColor: '#00a085',
      transform: 'translateY(-2px)'
    }
  };

  const policySections = {
    introduction: {
      title: "Introduction",
      content: (
        <div>
          <p style={textStyle}>
            Welcome to TimeOptics. These Terms and Conditions govern your use of our website and services. 
            By accessing or using our website, you agree to be bound by these Terms and Conditions.
          </p>
          
          <div style={highlightBoxStyle}>
            <strong>Important:</strong> Please read these Terms and Conditions carefully before using our website. 
            If you do not agree with any part of these terms, you must not use our website or services.
          </div>
          
          <p style={textStyle}>
            These Terms and Conditions apply to all visitors, users, and others who access or use our website 
            and services. We reserve the right to modify these terms at any time, so please check back periodically 
            for changes.
          </p>
        </div>
      )
    },
    accounts: {
      title: "User Accounts",
      content: (
        <div>
          <p style={textStyle}>
            To access certain features of our website, you may need to create a user account. 
            You are responsible for maintaining the confidentiality of your account information.
          </p>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Account Responsibilities:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You must provide accurate and complete information when creating an account</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You are responsible for all activities that occur under your account</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You must notify us immediately of any unauthorized use of your account</li>
            
          </ul>
          
          <div style={warningBoxStyle}>
            <strong>Account Termination:</strong> We reserve the right to suspend or terminate your account 
            if we suspect any fraudulent, abusive, or illegal activity.
          </div>
        </div>
      )
    },
    orders: {
      title: "Orders & Payments",
      content: (
        <div>
          <p style={textStyle}>
            All orders placed through our website are subject to acceptance and availability. 
            We reserve the right to refuse or cancel any order for any reason.
          </p>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Pricing and Payment:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> All prices are shown in INR unless otherwise indicated</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We accept various payment methods including credit cards and Razorpay</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> Your payment card will be charged when your order is shipped</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> For prescription eyewear, payment is required before customization begins</li>
          </ul>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Order Acceptance:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> Order confirmation does not constitute acceptance of your order</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We may require additional verification before accepting an order</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We reserve the right to limit or cancel quantities purchased per person</li>
          </ul>
        </div>
      )
    },
    products: {
      title: "Products & Services",
      content: (
        <div>
          <p style={textStyle}>
            We strive to display our products as accurately as possible, but please note that actual colors 
            and appearances may vary due to device display settings.
          </p>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Prescription Eyewear:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You are responsible for providing accurate prescription information</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We are not liable for vision problems resulting from incorrect prescription information</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> Prescription eyewear is custom-made and may have different return policies</li>
          </ul>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Product Availability:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> All products are subject to availability</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We reserve the right to discontinue any product at any time</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> In the event a product is unavailable, we may offer a substitute of equal quality and value</li>
          </ul>
          
          <div style={highlightBoxStyle}>
            <strong>Warranty Information:</strong> Most of our watches come with a manufacturer's warranty. 
            Prescription eyewear may have specific warranty coverage for lenses and frames. 
            Please refer to product pages for specific warranty details.
          </div>
        </div>
      )
    },
    intellectual: {
      title: "Intellectual Property",
      content: (
        <div>
          <p style={textStyle}>
            All content on this website, including text, graphics, logos, images, and software, 
            is the property of TimeOptics or its content suppliers and is protected by international 
            copyright and trademark laws.
          </p>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Permitted Use:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You may view, download, and print content for personal, non-commercial use</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You may share product information via social media with proper attribution</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> You may not reproduce, duplicate, or resell any content for commercial purposes</li>
          </ul>
          
          <div style={warningBoxStyle}>
            <strong>Copyright Notice:</strong> The TimeOptics name, logo, and all related product and service names 
            are trademarks of TimeOptics. You may not use these marks without our prior written permission.
          </div>
        </div>
      )
    },
    liability: {
      title: "Limitation of Liability",
      content: (
        <div>
          <p style={textStyle}>
            To the fullest extent permitted by law, TimeOptics shall not be liable for any indirect, 
            incidental, special, consequential, or punitive damages resulting from your use of or 
            inability to use our website or products.
          </p>
          
          <h3 style={{...sectionTitleStyle, fontSize: '1.4rem'}}>Product Liability:</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> We are not liable for any injuries resulting from improper use of our products</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> For prescription eyewear, we are not liable for vision problems resulting from incorrect prescription information provided by you</li>
            <li style={listItemStyle}><span style={listIconStyle}>•</span> Our liability for any claims related to our products is limited to the purchase price of the product</li>
          </ul>
          
          <div style={highlightBoxStyle}>
            <strong>Jurisdiction:</strong> These Terms and Conditions are governed by the laws of the State of California. 
            Any disputes shall be resolved in the courts located in San Francisco County, California.
          </div>
        </div>
      )
    }
  };

  // Add CSS for responsiveness
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    @media (max-width: 768px) {
      .terms-tab {
        padding: 10px 15px;
        font-size: 0.85rem;
      }
    }
    
    @media (max-width: 480px) {
      .terms-tab {
        padding: 8px 12px;
        font-size: 0.8rem;
      }
    }
    
    .last-updated {
      text-align: center;
      margin-top: 30px;
      color: #636e72;
      font-size: 0.9rem;
    }
  `;
  document.head.appendChild(styleElement);

  return (
    <>
      <Navbar />
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Terms and Conditions</h1>
          <p style={subtitleStyle}>
            Please read these terms carefully before using our website or services
          </p>
        </div>

        <div style={tabContainerStyle}>
          {Object.keys(policySections).map((key) => (
            <div 
              key={key}
              style={activeSection === key ? activeTabStyle : tabStyle}
              onClick={() => setActiveSection(key)}
              className="terms-tab"
            >
              {policySections[key].title}
            </div>
          ))}
        </div>

        <div style={contentStyle}>
          <h2 style={sectionTitleStyle}>{policySections[activeSection].title}</h2>
          {policySections[activeSection].content}
        </div>

        <div style={contactBoxStyle}>
          <h3 style={{...sectionTitleStyle, textAlign: 'center', border: 'none', marginBottom: '15px'}}>
            Questions about our Terms?
          </h3>
          <p style={{...textStyle, textAlign: 'center', marginBottom: '20px'}}>
            If you have any questions about these Terms and Conditions, please contact our legal team.
          </p>
          <Link 
            to="/ContactUs" 
            style={linkStyle}
          >
            Contact Us
          </Link>
        </div>

        <div className="last-updated">
          <p>Last updated: January 15, 2023</p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsAndConditions;